<script lang="ts">
	import classNames from 'classnames';
	import { type Component, onMount } from 'svelte';
	import { twMerge } from 'tailwind-merge';
	import { NavBar } from '../NavBar';
	import { DynamicCartIcon } from '../NavBar/DynamicCartIcon';
	import { getCartIconContext } from '#lib/features/checkout/utils/getCartIconContext/index.js';
	import { getCheckoutContext } from '#lib/features/checkout/utils/getCheckoutContext/index.js';
	import type { SiteNavLink } from '$global/features/navigation/types';
	import type { SearchClasses } from '$global/features/search/components/QuerySearch/Search';
	import type { SearchCombobox } from '$global/features/search/utils/createSearchCombobox';
	import { whenPageIdle } from '$global/utils/whenPageIdle/whenPageIdle';

	let {
		combobox,
		homeIcon: homeIconSnippet,
		wishlistIcon: wishlistIconSnippet,
		SearchComponent = undefined,
		isSearchEnabled = undefined,
		intermittentCart = false,
		disableCart = false,
		siteLinks = [],
		classes = {},
		...rest
	}: Props = $props();

	interface Props {
		homeIcon?: import('svelte').Snippet;
		// Optional extra nav icon (e.g. HENI Primary wishlist) rendered alongside
		// the cart icon. Sites that don't pass it are unaffected.
		wishlistIcon?: import('svelte').Snippet<[boolean]>;
		combobox: SearchCombobox | undefined;
		siteLinks?: SiteNavLink[];
		SearchComponent?:
			| Component<{
					combobox: SearchCombobox | undefined;
					onCloseSearch?: (() => void) | undefined;
					onclick?: () => void;
					onkeyup?: () => void;
					class?: string;
					classes?: SearchClasses | undefined;
			  }>
			| undefined;
		isSearchEnabled?: boolean | undefined;
		intermittentCart?: boolean | undefined;
		disableCart?: boolean | undefined;
		classes?: {
			bottomBar?: string;
		};
	}

	const { showCartIcon } = getCartIconContext() || {};

	// Controls if whether or not we should show the cart icon
	// Shown the cart is not intermittent (on Publishing or Merch)
	// Or when a drop is active ($showCartIcon === true)
	let shouldShowCartIcon = $derived(!intermittentCart || $showCartIcon);

	// The drawer (cart entries, checkout components, the Markdown renderer) is
	// kept out of the scripts every shop, editions, merch and primary page
	// preloads: it mounts once the page is idle, or when it is first opened if
	// that is sooner, and then stays mounted. It must mount on every page, not
	// only on open: its content keeps the cart in sync with the server
	// (upsertCart) whenever the cart changes, open or not.
	const {
		cartDrawer: {
			states: { open: cartDrawerOpen },
		},
	} = getCheckoutContext();

	let cartDrawerNeeded = $state(false);

	onMount(() =>
		whenPageIdle(() => {
			cartDrawerNeeded = true;
		})
	);

	$effect(() => {
		if ($cartDrawerOpen) {
			cartDrawerNeeded = true;
		}
	});
</script>

<NavBar
	{...rest}
	{siteLinks}
	{combobox}
	{SearchComponent}
	{isSearchEnabled}
	homeIcon={homeIconSnippet}
	classes={{
		...classes,
		bottomBarClasses: {
			siteLinks: shouldShowCartIcon ? '' : 'translate-x-[2.5rem]',
		},
	}}
>
	<!-- The cart icon is rendered twice but only of them is shown at a time according to the screen resolution -->
	{#snippet leftLink()}
		<div class="flex items-center">
			<!-- Wishlist icon mirrors the cart icon's responsive slot visibility:
			     this (left/TopBar) copy shows on mobile only. -->
			<div class="lg:hidden">
				{@render wishlistIconSnippet?.($showCartIcon)}
			</div>
			{#if $showCartIcon}
				<DynamicCartIcon
					{disableCart}
					dataCy="cart-icon"
					class="translate-y-[1px] lg:hidden"
				/>
			{/if}
		</div>
	{/snippet}
	{#snippet rightLink()}
		<div class="flex items-center">
			<!-- this (right) copy shows on desktop only -->
			<div class="hidden lg:block">
				{@render wishlistIconSnippet?.($showCartIcon)}
			</div>
			<DynamicCartIcon
				{disableCart}
				dataCy="cart-icon"
				class={twMerge(
					'invisible mr-[-0.125rem] ml-4 hidden lg:block',
					classNames({
						visible: shouldShowCartIcon,
					})
				)}
			/>
		</div>
	{/snippet}
</NavBar>

<!-- Renders the cart drawer is a drop is active -->
{#if $showCartIcon && cartDrawerNeeded}
	{#await import('#lib/features/checkout/components/CartDrawer/index.js') then { CartDrawer }}
		<CartDrawer />
	{/await}
{/if}
